import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Flex,
  Input,
  Popconfirm,
  Row,
  Spin,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { ArrowLeftOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import type { DescriptionsProps, TagProps } from 'antd';
import {
  Children,
  forwardRef,
  useMemo,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ERROR_CODE, ERROR_MESSAGE_KEY, type HttpError } from '@/shared/api/errors';
import { sumAmountTriples } from '@/shared/domain/money';
export interface PageHeaderProps {
  title: string;
  extra?: ReactNode;
  onBack?: () => void;
}
export function PageHeader({ title, extra, onBack }: PageHeaderProps) {
  const { t } = useTranslation();
  return (
    <Flex align="center" justify="space-between" wrap gap="small" style={{ minHeight: 32 }}>
      <Flex align="center" gap="small">
        {onBack !== undefined && (
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined />}
            aria-label={t('common.back')}
            onClick={onBack}
          />
        )}
        <Typography.Title level={4} style={{ margin: 0 }}>
          {title}
        </Typography.Title>
      </Flex>
      {extra !== undefined && (
        <Flex align="center" wrap gap="small">
          {extra}
        </Flex>
      )}
    </Flex>
  );
}
export function ErrorState({ error, onRetry }: { error: HttpError; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <Alert
      type={
        error.code === ERROR_CODE.validation || error.code === ERROR_CODE.conflict
          ? 'warning'
          : 'error'
      }
      showIcon
      message={t(ERROR_MESSAGE_KEY[error.code])}
      description={
        error.status === 0 ? undefined : (
          <NoteText>{error.requestId ?? `HTTP ${error.status}`}</NoteText>
        )
      }
      action={
        onRetry === undefined ? undefined : (
          <Button size="small" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        )
      }
    />
  );
}
export function TableEmpty({ label }: { label: string }) {
  return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={label} />;
}
/** 表格**单元格**里的空值。刻意与 `TableEmpty`（表格空态）分开：
 *  一个是「这页没数据」，一个是「这个字段没值」。混用会让用户分不清。 */
export function EmptyCell() {
  return <Typography.Text type="secondary">—</Typography.Text>;
}

/**
 * 辅助小字——「一小段灰色说明」的**唯一**写法。
 *
 * ## 为什么抽出来
 *
 * 此前**六处**各写一遍 `<Typography.Text type="secondary" style={{ fontSize: 12 }}>`：
 * AppLayout 的流状态详情、`ErrorState` 的 requestId、`StatChips` 的标签、
 * 通知列表的时间与错误码、授权弹窗的范围清单。
 *
 * 同一句话写六遍 ⇒ 改字号要改六处、漏一处就不齐 ⇒ 收成一处。
 *
 * ## 字号走 token
 *
 * `12` 是 antd `token.fontSizeSM` 的**默认值**——写死数字就绕过了 token
 * （将来改主题字号，这六处不会跟着变）⇒ 这里取 `token.fontSizeSM`。
 *
 * ⚠️ 「小字」不是纯装饰：12px 灰字承载的是**次要信息**（时间、错误码、清单），
 * 它在信息层级里的位置必须**全树一致**，否则同一类信息在 A 页面是脚注、在 B 页面
 * 却跟正文一样大，读的人要多花一次判断。
 */
export function NoteText({
  children,
  copyable,
}: {
  children: ReactNode;
  /** 复制按钮（`Typography.Text` 的 `copyable` 原样透传）。 */
  copyable?: ComponentProps<typeof Typography.Text>['copyable'];
}) {
  const { token } = theme.useToken();
  return (
    <Typography.Text
      type="secondary"
      style={{ fontSize: token.fontSizeSM }}
      {...(copyable === undefined ? {} : { copyable })}
    >
      {children}
    </Typography.Text>
  );
}

/** 弹窗里「占满一整行」的控件宽度——全树一份。 */
export const FULL_WIDTH: CSSProperties = { width: '100%' };

/**
 * 表格单元格里的**长文本**：块级 + 允许在任意处断行。
 *
 * ## 为什么
 *
 * antd 的 `Typography.Text` 渲染成 `<span>`（**行内**）⇒ 一段没有空格的长文本
 * （Steam 的失败原因、诊断串）会**顶出单元格**，压到下一列上。
 *
 * ⚠️ 此前有人试图用 `style={{ width: 列宽 − 8 }}` 来约束它——但 **width 不作用于
 * 非替换行内元素** ⇒ 那条声明**从来没生效过**，却让人以为宽度受控（实际比单元格
 * 内容盒宽 16px）。要让文字按单元格内容盒换行，只能给它**块级**。
 *
 * ## 为什么是两个属性
 *
 * - `display: block` —— 让它成为块盒、按内容盒宽度排版；
 * - `wordBreak: break-word` —— 让**没有空格**的一长串（英文诊断串、URL）也能断。
 */
export const CELL_TEXT_WRAP: CSSProperties = { display: 'block', wordBreak: 'break-word' };
/**
 * 码列文本（钱包码 / 订单号这类机器码）。antd 的 `code` 变体上游写死
 * `fontSize: 85%`（无 token 可配），表格正文 14px 下码只剩 ~11.9px——
 * 码是要人眼核对抄录的，这里顶回与正文齐平还不够：
 *
 * - **放大**：抬到 `fontSizeLG`（16px）。11.9px 的码在一屏表格里几乎读不出
 *   笔画差别，抄错的代价是真金白银。
 * - **字体跟账号列一致**（2026-10-26 用户裁定）：用**应用正文字体**
 *   （`token.fontFamily`），不是等宽的机器码字体 —— 钱包码与账号是同一类东西
 *   （都是要人抄、要人核的凭据），同款字体在一屏表格里才不显得钱包码更扎眼。
 * - **不加粗**（同上裁定）：码**不套** `<strong>` ⇒ 与账号列同为常规字重。
 *
 * ## 字号为什么要落两处
 *
 * antd 的 `.ant-typography code` 写死 `font-size: 85%` ⇒ 字号是**相对**外层
 * `<span class="ant-typography">` 的：外层 16px 时，内层 `<code>` 只有 13.6px。
 * ⇒ 外层给 16px（决定 `<code>` 的内边距与复制按钮大小），**文字本身**再给一层
 * 16px（内层 `<span>`），字号才是真 16px。
 *
 * ## 字体为什么也落两处
 *
 * 同一个 `.ant-typography code` 规则把**字体**也写死成 `fontFamilyCode`（等宽那款）
 * ⇒ 只改外层没用（内层 `<code>` 自带字体）⇒ 字体也放在**内层 `<span>`** 上。
 *
 * ⚠️ **代价，明说**：正文字体的零**没有斜杠** ⇒ `O` 与 `0` 只能靠字形宽窄分辨，
 * 不再是「一眼可分」。若哪天又要一眼可分，得**只给零做记号**（而不是整列换
 * 等宽字体）—— 那条路留着。
 *
 * ## 复制为什么显式给 text
 *
 * antd 的复制文本取自 `children`（`toList(children).join('')`）——children 是
 * 元素时 join 出来是 `"[object Object]"` ⇒ 复制出去不是这串码。
 * ⇒ 显式 `copyable={{ text: value }}`。
 *
 * ⚠️ `style` 走模块内的对象而不是 JSX 内联字面量：本树不写自定义 CSS，
 * 内联 style 有预算棘轮（`scripts/check-ui-discipline.mjs`）。
 */
export function CodeText({ value }: { value: string }) {
  const { token } = theme.useToken();
  const style: CSSProperties = { fontSize: token.fontSizeLG, fontFamily: token.fontFamily };
  return (
    <Typography.Text code copyable={{ text: value }} style={style}>
      <span style={style}>{value}</span>
    </Typography.Text>
  );
}
const STATS_COLUMNS = { xs: 1, sm: 2, md: 3 } as const;
/**
 * 状态徽标。`color` 用 antd 预设色板名（`success` / `warning` / …），
 * 不写 hex —— 写死就绕过了 token，深色模式下会瞎。
 */
export function StatusPill({
  label,
  color,
}: {
  label: string;
  color: NonNullable<TagProps['color']>;
}) {
  return <Tag color={color}>{label}</Tag>;
}
/**
 * 金额标签。
 *
 * ⚠️ `text` 的类型是 `string | null` 而**不是** `string | undefined` ——
 * 后端 `AmountTriple.text` 在 spec 里是 `anyOf[string, null]`。
 * 直接传后端金额对象会**编译不过**（`null` 不是 `undefined`）——
 * 类型要收成 `string | null`，不能写 `text?: string`。
 *
 * ⇒ 类型要照 wire 写，不是照自己想的样子写。
 */
/**
 * 币种配色。
 *
 * ⚠️ 这是**信息编码**不是装饰：表格里 USD / CNY / EUR 三种币种同屏出现时，
 * 颜色是最快的区分方式；去掉之后「30 USD」和「30 CNY」只能靠读后缀。
 */
const AMOUNT_UNIT_COLOR: Record<string, TagProps['color']> = {
  USD: 'green',
  CNY: 'volcano',
  EUR: 'purple',
};
export function AmountTags({
  amounts,
  sumByUnit = true,
  suffix,
}: {
  amounts: readonly { text?: string | null; value: string; unit: string }[];
  /**
   * 同币种多笔合并成一个总额（默认开）。
   *
   * 关掉它的地方传 `false`：那是「这几笔是不同的东西、不该加在一起」
   * 的场景（例如逐单的余额变动）。
   */
  sumByUnit?: boolean;
  /**
   * 金额**之后**的附加内容（排在 Tag 后面）——例如括号里的 USD 口径。
   *
   * ⚠️ 为什么不是一个 prop 一个金额：这里只承接「主金额 + 一个括注」这种
   * 固定形状（`8000.00 IDR (0.5000 USD)`）⇒ 用 ReactNode 让调用方自己拼，
   * 比在本件里塞一个「USD 口径」的专用参数更通用。
   */
  suffix?: ReactNode;
}) {
  if (amounts.length === 0 && suffix === undefined) return <EmptyCell />;
  const ordered = (
    sumByUnit ? sumAmountTriples(amounts.map((triple) => [triple])) : [...amounts]
  ).sort((a, b) => (a.unit < b.unit ? -1 : a.unit > b.unit ? 1 : 0));
  return (
    <Flex gap={4} wrap style={{ fontVariantNumeric: 'tabular-nums' }}>
      {ordered.map((item, index) => (
        <Tag key={index} variant="filled" color={AMOUNT_UNIT_COLOR[item.unit] ?? 'default'}>
          {item.text ?? `${item.value} ${item.unit}`}
        </Tag>
      ))}
      {suffix}
    </Flex>
  );
}
export type ActionKind = 'modal' | 'navigate' | 'selection' | 'destructive';
export interface ActionConfirm {
  title: string;
  okText: string;
  cancelText: string;
  okDanger?: boolean;
}
/**
 * 带二次确认的按钮 —— 全树**唯一**的确认按钮实现。
 *
 * ## 为什么不让调用方用 `window.confirm`
 *
 * 浏览器原生对话框与全站 antd 体系割裂，而且**按钮文案跟随浏览器语言**而不是
 * 应用语言（中英界面下同一句确认会变成两种文字）。
 *
 * ## 与 `ActionButton` 的关系
 *
 * `ActionButton` 的 `confirm` 分支就是本组件 —— 收敛成一处，
 * 「批量条上的确认」与「行内动作上的确认」不会走出两种观感。
 */
export interface ConfirmButtonProps {
  children: ReactNode;
  /** 点「确定」后执行。 */
  onConfirm: () => void;
  confirm: ActionConfirm;
  disabled?: boolean;
  loading?: boolean;
  type?: 'primary' | 'default' | 'link' | 'text';
  /** 按钮本身是危险色（与 `confirm.okDanger` 分开：前者看动作，后者看后果）。 */
  danger?: boolean;
  size?: 'small' | 'middle' | 'large';
  icon?: ReactNode;
  className?: string;
}
export function ConfirmButton({
  children,
  onConfirm,
  confirm,
  disabled = false,
  loading = false,
  type,
  danger = false,
  size,
  icon,
  className,
}: ConfirmButtonProps) {
  return (
    <Popconfirm
      title={confirm.title}
      okText={confirm.okText}
      cancelText={confirm.cancelText}
      okButtonProps={{ danger: confirm.okDanger === true }}
      disabled={disabled || loading}
      onConfirm={onConfirm}
    >
      {/* `exactOptionalPropertyTypes`：可选 prop 一律条件展开，不直接传 undefined */}
      <Button
        {...(type === undefined ? {} : { type })}
        {...(danger === false ? {} : { danger })}
        {...(size === undefined ? {} : { size })}
        {...(icon === undefined ? {} : { icon })}
        {...(className === undefined ? {} : { className })}
        disabled={disabled}
        loading={loading}
      >
        {children}
      </Button>
    </Popconfirm>
  );
}
export interface ActionButtonProps {
  /** 图标形态可不传（icon-only）—— 此时 `ariaLabel` 必给，否则按钮没有可访问名。 */
  children?: ReactNode;
  onAction: () => void;
  kinds?: readonly ActionKind[];
  selectedCount?: number;
  confirm?: ActionConfirm;
  disabled?: boolean;
  danger?: boolean;
  type?: 'primary' | 'default' | 'link' | 'text';
  size?: 'small' | 'middle' | 'large';
  icon?: ReactNode;
  /** 无障碍名。文字按钮的名称来自 children，不必传；图标按钮必须传。 */
  ariaLabel?: string;
  /**
   * 提交中。
   *
   * 漏掉它会让行内「删除/登出」这类异步操作在提交期间没有等待反馈 —— 用户连点两下就发两次。宽接口换成窄接口时，**要逐字段对一遍**。
   */
  loading?: boolean;
}
/**
 * 行内/工具栏动作按钮。
 *
 * `selection` 档位在**没选中任何行时自动禁用** —— 那是最容易漏的一处：
 * 批量删除按钮在 0 选中时可点，点了才发现没有 id 可提交。
 */
export function ActionButton({
  children,
  onAction,
  kinds = [],
  selectedCount = 0,
  confirm,
  disabled,
  type,
  size,
  icon,
  ariaLabel,
  loading = false,
}: ActionButtonProps) {
  const modal = kinds.includes('modal');
  const navigate = kinds.includes('navigate');
  const needsSelection = kinds.includes('selection');
  const destructive = kinds.includes('destructive');
  const isDisabled = disabled === true || (needsSelection && selectedCount === 0);
  const button = (
    <Button
      type={type ?? (navigate ? 'link' : modal ? 'primary' : 'default')}
      size={size ?? (needsSelection ? 'small' : undefined)}
      danger={destructive}
      disabled={isDisabled || loading}
      loading={loading}
      icon={icon}
      aria-haspopup={modal ? 'dialog' : undefined}
      aria-label={ariaLabel}
      onClick={confirm === undefined ? onAction : undefined}
    >
      {children}
    </Button>
  );
  if (confirm === undefined) return button;
  return (
    <Popconfirm
      title={confirm.title}
      okText={confirm.okText}
      cancelText={confirm.cancelText}
      okButtonProps={{ danger: confirm.okDanger === true }}
      disabled={isDisabled}
      onConfirm={onAction}
    >
      {button}
    </Popconfirm>
  );
}
export const DETAIL_COLUMNS: DescriptionsProps['column'] = { xs: 1, md: 2, xl: 3 };
type AntdItem = NonNullable<DescriptionsProps['items']>[number];
export type DetailItem = AntdItem;
/**
 * 详情卡（含 `stretch`）。
 *
 * ⚠️ `stretch` 用 `height: 100%` 让同排卡片等高：两栏并排时，右栏内容少 ⇒ 卡片比左栏矮、底部对不齐。
 */
export const STRETCH: CSSProperties = { height: '100%' };
export function DetailCard({
  title,
  items,
  column = DETAIL_COLUMNS,
  stretch = false,
}: {
  title: ReactNode;
  items: ReadonlyArray<DetailItem>;
  column?: DescriptionsProps['column'];
  stretch?: boolean;
}) {
  const descProps = { size: 'small' as const, items: [...items] };
  return (
    <Card size="small" title={title} {...(stretch ? { style: STRETCH } : {})}>
      <Descriptions {...descProps} {...(column === undefined ? {} : { column })} />
    </Card>
  );
}
export function FieldActions({ children, actions }: { children: ReactNode; actions: ReactNode }) {
  return (
    <Flex align="center" gap="small" wrap>
      {children}
      {actions}
    </Flex>
  );
}
export function StatChips({ items }: { items: readonly { label: string; value: ReactNode }[] }) {
  return (
    <Flex gap="large" wrap>
      {items.map((item) => (
        <Flex key={item.label} vertical gap={2}>
          <NoteText>{item.label}</NoteText>
          <Typography.Text strong>{item.value}</Typography.Text>
        </Flex>
      ))}
    </Flex>
  );
}
/**
 * 统计区里的一格**直方图** —— 每档一个带色 `<Tag>`，横向平铺。
 *
 * ⚠️ 它与上面 `StatChips` 不是一回事：那个渲染「一格 = 标签 + 值」，
 * 这个渲染「一格 = 多个带色档位」（角色 / 在线 / 启用 / 协议…）。
 * 拆成两个组件是为了避免一个组件两种职责。
 *
 * 每档一色，颜色是视觉锚点。
 */
export interface StatHistogramRow {
  key: string;
  label: string;
  count: number;
  color: TagProps['color'] | undefined;
}
export function StatHistogram({ rows }: { rows: readonly StatHistogramRow[] }) {
  return (
    <Flex gap={4} wrap>
      {rows.map((row) => (
        <Tag key={row.key} {...(row.color === undefined ? {} : { color: row.color })}>
          {row.label} {row.count}
        </Tag>
      ))}
    </Flex>
  );
}
/**
 * 统计折叠区。
 *
 * ⚠️ `error` 与 `onRetry` **都是必填**：忘了传就**编译不过**，而不是统计请求失败时静默显示空 —— 用户看到「全是 0」，而真实情况是「没查到」。
 *
 * 统计项由调用方给一个映射函数（`data => items`），因为每个域的统计
 * 字段不同；收敛的只是「加载中 / 出错 / 有数据」三态。
 */
/**
 * 行内动作的**换行布局**。
 *
 * ## 差别在哪
 *
 * 裸 `Flex wrap` 是**自然换行**：按钮宽度不一，凑不下就断在某处，
 * 于是最后一行可能只剩一颗（「参差」）。
 *
 * 这里超过 3 颗就**均分成两行**（`ceil(n/2)` + 余下）—— 两行长度差最多 1，肉眼看上去是齐的。
 *
 * ⚠️ 操作列窄 + 按钮多的时候差别很明显：4 颗按钮是 2+2，自然换行可能变成 3+1。
 */
/**
 * 筛选控件的**共享宽度表** —— 同一类控件在不同页必须同宽。
 *
 * ⚠️ 同一类控件在不同页宽度必须相同：17 处筛选框各写各的宽度（170 / 180 / 200 / 220 / 130 / 160 …）会让同控件在不同页不等宽（实测用户页 130 vs CDKey 180）。
 *
 * 1. **同控件不等宽** 2. **重复** —— 同一个「关键词框该多宽」的决定散在 17 处
 *
 * ⇒ 判据与 AGENTS.md 第 1 条一致：看见三份同形就收成一个。
 * ⚠️ 值**不能改**（既有约定）；要调先问。
 */
export const FILTER_WIDTH = {
  /** 关键词输入框（用户名 / 卡号 / 订单号 …） */
  keyword: 180,
  /** 下拉选择（角色 / 状态 / 协议 …） */
  choice: 120,
  /** 时间区间 */
  range: 240,
} as const;
/** 列表页操作列的统一列宽（2026-10-19 用户裁定：全前端一律 180）。
 *  弹窗内迷你表的操作列不在此列 —— 那是局部上下文，不追齐列表页。 */
export const ACTIONS_COL_WIDTH = 180;
export function ActionFlow({ children }: { children: ReactNode }) {
  const items = Children.toArray(children);
  if (items.length <= 3)
    return (
      <Flex align="center" gap={4}>
        {items}
      </Flex>
    );
  const half = Math.ceil(items.length / 2);
  return (
    <Flex vertical gap={2}>
      <Flex align="center" gap={4}>
        {items.slice(0, half)}
      </Flex>
      <Flex align="center" gap={4}>
        {items.slice(half)}
      </Flex>
    </Flex>
  );
}
/**
 * 固定高度的多行输入。
 *
 * ## 为什么要它，而不是直接用 `Input.TextArea`
 *
 * 差别只有一处，但那一处是**看得见的**：裸 `TextArea` 右下角带一个拖拽手柄，
 * 用户一拖，**整个表单的高度就变了**。列表页的弹窗是固定高度设计的，
 * 拖一下版式就散了。
 *
 * ⚠️ 全树建单 / 建代理 / 通知抽屉三处都用了裸 `Input.TextArea`，于是都带着那个手柄。
 */
const FIXED_TEXTAREA: CSSProperties = { resize: 'none' };
export function FixedTextArea({ rows = 6, ...rest }: ComponentProps<typeof Input.TextArea>) {
  return <Input.TextArea rows={rows} style={FIXED_TEXTAREA} {...rest} />;
}
/**
 * 批量选择栏的**左半截**。
 *
 * ## 两种形态的分界
 *
 * 每页各写一段「已选 N 项」+ 一颗**常驻的禁用**「清空选择」按钮，有两处偏离：
 *
 * | | 0 选中时 | 选中数 |
 * |---|---|---|
 * | 只渲染提示文字，**清空按钮根本不出现** | 一颗 `<Badge count>` |
 *
 * 第二条看着像小事，其实不一样：Badge 是**视觉锚点**，扫一眼表格就知道
 * 选了几条；混在句子里就得读字。
 */
export function SelectionBar({
  count,
  idleText,
  clearText,
  ariaLabel,
  onClear,
}: {
  count: number;
  idleText: string;
  clearText: string;
  /** 无障碍名。`role="region"` 需要它。 */
  ariaLabel?: string;
  onClear: () => void;
}) {
  const { token } = theme.useToken();
  if (count === 0) {
    return (
      <Flex role="region" aria-label={ariaLabel} align="center">
        <Typography.Text type="secondary">{idleText}</Typography.Text>
      </Flex>
    );
  }
  return (
    <Flex role="region" aria-label={ariaLabel} align="center" wrap gap="small">
      <Badge count={count} color={token.colorPrimary} />
      <Button size="small" type="text" onClick={onClear}>
        {clearText}
      </Button>
    </Flex>
  );
}
/**
 * 统计折叠开关。
 *
 * ## 为什么单独抽出来
 *
 * 五处各写一个 `<Button size="small">{statsOpen ? '收起统计' : '统计'}</Button>`，有**两处**偏离：
 *
 * | | 文案 | 图标 |
 * |---|---|---|
 * | 恒为「统计概览」（`stats.title`），**不随开合变** | 末尾有 `Down/UpOutlined`（`iconPlacement="end"`） |
 *
 * ⚠️ 文案那条尤其值得记：按钮**始终说同一句话** —— 箭头已经表达了开合，再让文字也变，等于把同一个信息说两遍。
 */
export function StatsToggle({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      type="text"
      size="small"
      iconPlacement="end"
      icon={open ? <UpOutlined /> : <DownOutlined />}
      aria-expanded={open}
      onClick={onToggle}
    >
      {t('stats.title')}
    </Button>
  );
}
/** 统计项 —— `span` 是占几格。 */
export interface StatItem {
  label: string;
  value: ReactNode;
  span?: number | undefined;
}
/**
 * 一格排不下的宽项：**先补空位再换行**。
 *
 * ⚠️ 不补位会怎样（实测，不是推断）：3 列栅格下 `span` 序列
 * `[1,1,2,1,1,1,1,2]` 渲染成
 *
 * ```
 * 行1: 总数  订单数  [空]
 * 行2: 账号数(2 格)  需充金额
 * ```
 *
 * 「账号数」被挤到第二行，于是**第一行右侧留一个空洞**，而下面几行都是满的 ——
 * 看上去像少了一格。补位后 `账号数` 回到行1 尾部，四行全部对齐。
 */
function layoutCells(
  spans: readonly (number | undefined)[],
  column: number,
): readonly { index: number | null; span: number }[] {
  const clamp = (span: number | undefined): number => Math.min(Math.max(span ?? 1, 1), column);
  const out: { index: number | null; span: number }[] = [];
  let count = 0;
  spans.forEach((declared, index) => {
    const want = clamp(declared);
    if (count > 0 && count + want > column) {
      out.push({ index: null, span: column - count });
      count = 0;
    }
    out.push({ index, span: want });
    count += want;
    if (count >= column) count = 0;
  });
  if (count > 0) out.push({ index: null, span: column - count });
  return out;
}
export function StatsOverview<S>({
  data,
  total,
  renderItems,
  loading,
  error,
  onRetry,
}: {
  data: S | undefined;
  /**
   * 「全部结果 N 条」——**可见集合**的总数，而上面的 chips 各有自己的过滤维度
   * （状态 / 阶段 / 协议…）。
   *
   * 不传则不渲染这一行 —— 有些统计的「总数」没有意义（如按可见性分组的代理表）。
   */
  total?: number | undefined;
  /**
   * 统计项。`span` 是**占几格**（1 = 一格，2 = 两格）——
   * `span` 让宽项（金额合计、长直方图）拿到两格，避免被挤成半格。栅格是 `STATS_COLUMNS = {xs:1, sm:2, md:3}`。
   */
  renderItems: (data: S) => readonly StatItem[];
  loading: boolean;
  error: HttpError | null;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const items = data === undefined ? [] : renderItems(data);
  if (data === undefined && !loading) return null;
  if (items.length === 0 && !loading && error === null) return null;
  const columns = STATS_COLUMNS.md;
  const unit = 24 / columns;
  return (
    <Flex vertical gap={2}>
      <Spin spinning={loading}>
        {error !== null ? (
          /* 失败时**留在卡里**：卡在、只换卡内文案。 不要 `return <ErrorState/>` —— 而 Card 在调用方，于是失败瞬间整张卡连同统计格一起消失，页面结构突变、下方内容上跳。 */
          <Flex align="center" gap="small" wrap>
            <Typography.Text type="danger">{t('stats.loadFailed')}</Typography.Text>
            <Button size="small" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          </Flex>
        ) : (
          <Row gutter={[8, 12]}>
            {layoutCells(
              items.map((item) => item.span),
              columns,
            ).map((cell, position) => {
              const item = cell.index === null ? undefined : items[cell.index];
              return (
                <Col
                  key={item === undefined ? `filler-${position}` : item.label}
                  span={cell.span * unit}
                >
                  {item === undefined ? null : (
                    <Flex vertical gap={4}>
                      {/* 半粗灰字，不是 12px 脚注 */}
                      <Typography.Text type="secondary" style={{ fontWeight: 500 }}>
                        {item.label}
                      </Typography.Text>
                      {item.value}
                    </Flex>
                  )}
                </Col>
              );
            })}
          </Row>
        )}
      </Spin>
      {total !== undefined && (
        <Typography.Text type="secondary">
          {t('stats.totalScope', { count: total })}
        </Typography.Text>
      )}
    </Flex>
  );
}
export { useMemo, theme, Tooltip, forwardRef };
